<p-progressBar mode="indeterminate" *ngIf="loading"></p-progressBar>

<app-page-shell *ngIf="!loading">
  <app-page-hero
    [eyebrow]="'fieldWork.eyebrow' | transloco"
    [title]="pageTitle"
    [subtitle]="pageSubtitle">
    <div actions>
      <p-button [label]="'fieldWork.refresh' | transloco" icon="pi pi-refresh"
        severity="secondary" [outlined]="true" (onClick)="refresh()"></p-button>
    </div>
  </app-page-hero>

  <div class="error-banner" *ngIf="error"><i class="pi pi-exclamation-circle"></i>{{ error }}</div>

  <app-page-metric-strip *ngIf="canViewFieldWork" [metrics]="metrics"></app-page-metric-strip>

  <app-page-data-panel *ngIf="page === 'people'"
    title="Field people directory"
    subtitle="Operational workers are mastered here. Internal employees may keep an optional HR identity link, but assignment eligibility uses Field Work capabilities."
    [resultText]="peopleTotalRecords + ' people'">
    <div actions *ngIf="canManageFieldWork">
      <p-button label="Assign capability" icon="pi pi-award" severity="secondary" [outlined]="true"
        (onClick)="openWorkerCapabilityDialog()"></p-button>
      <p-button label="Add field person" icon="pi pi-user-plus" (onClick)="openPersonDialog()"></p-button>
    </div>
    <p-table [value]="people" [lazy]="true" [paginator]="true" [rows]="peopleRows" [first]="peopleFirst"
      [totalRecords]="peopleTotalRecords" [loading]="peopleTableLoading" [rowsPerPageOptions]="[10, 20, 50, 100]"
      [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} people"
      (onLazyLoad)="loadPeople($event)" dataKey="fieldWorkerId" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Person</th><th>Contact</th><th>Source</th><th>Category</th><th>Capabilities</th><th>Location</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.displayName }}</strong><small>{{ row.workerNumber }}</small></td>
        <td><a *ngIf="row.phoneNumber" [href]="'tel:' + row.phoneNumber"><i class="pi pi-phone"></i> {{ row.phoneNumber }}</a>
          <a *ngIf="row.emailAddress" [href]="'mailto:' + row.emailAddress"><i class="pi pi-envelope"></i> {{ row.emailAddress }}</a>
          <small *ngIf="!row.phoneNumber && !row.emailAddress">No contact recorded</small></td>
        <td><p-tag [value]="row.sourceType" [severity]="workerSourceSeverity(row.sourceType)"></p-tag>
          <small *ngIf="row.hrEmployeeId">HR identity #{{ row.hrEmployeeId }}</small>
          <small *ngIf="row.supplierCrmClientId">Supplier #{{ row.supplierCrmClientId }}</small></td>
        <td>{{ row.workerCategory || '—' }}</td>
        <td><strong>{{ row.capabilities?.length || 0 }} active records</strong>
          <small>{{ (row.capabilities || [])[0]?.capabilityName || 'No capabilities assigned' }}</small></td>
        <td>{{ row.currentLocationName || row.homeLocationName || '—' }}</td>
        <td><p-tag [value]="row.status || 'ACTIVE'" [severity]="row.status === 'ACTIVE' ? 'success' : 'secondary'"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No field people registered.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'setup'"
    title="Roles, capabilities, proficiency, and equipment categories"
    subtitle="Field Work setup is independent of HR. It defines operational roles, eligibility capabilities, ordered proficiency, and reusable equipment categories."
    [resultText]="roles.length + ' roles · ' + capabilities.length + ' capabilities · ' + categories.length + ' equipment categories'">
    <div actions>
      <p-button *ngIf="activeSetupTab === 'role' && canManageFieldWork" label="Add role" icon="pi pi-plus" (onClick)="openSetupDialog('role')"></p-button>
      <p-button *ngIf="activeSetupTab === 'capability' && canManageFieldWork" label="Add capability" icon="pi pi-plus" (onClick)="openSetupDialog('capability')"></p-button>
      <p-button *ngIf="activeSetupTab === 'proficiency' && canManageFieldWork" label="Add level" icon="pi pi-plus" (onClick)="openSetupDialog('proficiency')"></p-button>
      <p-button *ngIf="activeSetupTab === 'equipment-category' && canManageEquipment" label="Add equipment category" icon="pi pi-plus" (onClick)="openSetupDialog('equipment-category')"></p-button>
    </div>
    <p-tabs [value]="activeSetupTab" (valueChange)="onSetupTabChange($event)">
      <p-tablist>
        <p-tab value="role"><i class="pi pi-briefcase"></i><span>Roles</span><p-tag [value]="roles.length.toString()" severity="secondary"></p-tag></p-tab>
        <p-tab value="capability"><i class="pi pi-award"></i><span>Capabilities</span><p-tag [value]="capabilities.length.toString()" severity="secondary"></p-tag></p-tab>
        <p-tab value="proficiency"><i class="pi pi-chart-line"></i><span>Proficiency</span><p-tag [value]="proficiencyLevels.length.toString()" severity="secondary"></p-tag></p-tab>
        <p-tab value="equipment-category"><i class="pi pi-wrench"></i><span>Equipment categories</span><p-tag [value]="categories.length.toString()" severity="secondary"></p-tag></p-tab>
      </p-tablist>
      <p-tabpanels>
        <p-tabpanel value="role"><p-table [value]="roles" responsiveLayout="scroll">
          <ng-template pTemplate="header"><tr><th>Operational role</th><th>Worker category</th><th>Mandatory capabilities</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td><strong>{{ row.roleName }}</strong><small>{{ row.roleCode }}</small></td><td>{{ row.workerCategory || 'Any' }}</td><td>{{ roleCapabilityLabels(row) }}</td></tr></ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="3" class="empty-state">No roles configured.</td></tr></ng-template>
        </p-table></p-tabpanel>
        <p-tabpanel value="capability"><p-table [value]="capabilities" responsiveLayout="scroll">
          <ng-template pTemplate="header"><tr><th>Capability</th><th>Category</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td><strong>{{ row.capabilityName }}</strong><small>{{ row.capabilityCode }}</small></td><td>{{ row.capabilityCategory || 'General' }}</td></tr></ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="2" class="empty-state">No capabilities configured.</td></tr></ng-template>
        </p-table></p-tabpanel>
        <p-tabpanel value="proficiency"><p-table [value]="proficiencyLevels" responsiveLayout="scroll">
          <ng-template pTemplate="header"><tr><th>Proficiency</th><th>Rank</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td><strong>{{ row.levelName }}</strong><small>{{ row.levelCode }}</small></td><td>{{ row.levelRank }}</td></tr></ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="2" class="empty-state">No levels configured.</td></tr></ng-template>
        </p-table></p-tabpanel>
        <p-tabpanel value="equipment-category"><p-table [value]="categories" responsiveLayout="scroll">
          <ng-template pTemplate="header"><tr><th>Equipment category</th><th>Tracking</th></tr></ng-template>
          <ng-template pTemplate="body" let-row><tr><td><strong>{{ row.categoryName }}</strong><small>{{ row.categoryCode }}</small></td><td>{{ row.trackingMode }}</td></tr></ng-template>
          <ng-template pTemplate="emptymessage"><tr><td colspan="2" class="empty-state">No equipment categories configured.</td></tr></ng-template>
        </p-table></p-tabpanel>
      </p-tabpanels>
    </p-tabs>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'packages'"
    title="Work packages"
    subtitle="Packages can be added to existing shipments, POSM schedules, deliveries, containers, warehouse work, clearance jobs, and projects."
    [resultText]="packages.length + ' packages'">
    <p-table [value]="packages" [paginator]="packages.length > 20" [rows]="20"
      dataKey="fieldWorkPackageId" responsiveLayout="scroll">
      <ng-template pTemplate="header">
        <tr>
          <th>Package</th><th>Activity</th><th>Operational target</th><th>Planned window</th>
          <th>Coverage</th><th>Status</th><th class="action-column">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td>
            <button type="button" class="record-link" (click)="openPackage(row)">{{ row.workPackageNumber }}</button>
            <small>{{ row.title }}</small>
          </td>
          <td>{{ row.activityType || 'GENERAL' }}</td>
          <td><strong>{{ row.targetReference || row.targetType }}</strong><small>{{ row.targetType }}</small></td>
          <td>{{ row.plannedStart | date:'medium' }}<small>to {{ row.plannedEnd | date:'medium' }}</small></td>
          <td>
            <span class="coverage-line"><i class="pi pi-users"></i>{{ assignedLabor(row) }} / {{ requiredLabor(row) }} labor</span>
            <small><i class="pi pi-wrench"></i>{{ allocatedEquipment(row) }} / {{ requiredEquipment(row) }} equipment</small>
          </td>
          <td><p-tag [value]="row.status || 'DRAFT'" [severity]="packageStatus(row.status)"></p-tag></td>
          <td class="action-column">
            <p-button icon="fa-solid fa-eye" [rounded]="true" [text]="true" size="small"
              ariaLabel="View work package" (onClick)="openPackage(row)"></p-button>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="7" class="empty-state"><i class="pi pi-briefcase"></i><strong>No work packages</strong><span>Add field resources from a supported operational record.</span></td></tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'schedule'"
    title="Resource schedule"
    subtitle="A chronological view of field activity, location, operational target, and resource coverage."
    [resultText]="packages.length + ' scheduled packages'">
    <p-table [value]="packages" [paginator]="packages.length > 20" [rows]="20"
      dataKey="fieldWorkPackageId" sortField="plannedStart" [sortOrder]="1" responsiveLayout="scroll">
      <ng-template pTemplate="header">
        <tr><th pSortableColumn="plannedStart">Start <p-sortIcon field="plannedStart"></p-sortIcon></th>
          <th>Package</th><th>Location</th><th>Target</th><th>Labor</th><th>Equipment</th><th>Status</th></tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr class="navigable-row" (click)="openPackage(row)">
          <td><strong>{{ row.plannedStart | date:'mediumDate' }}</strong><small>{{ row.plannedStart | date:'shortTime' }} – {{ row.plannedEnd | date:'shortTime' }}</small></td>
          <td><span class="activity-icon"><i class="pi pi-calendar"></i></span><strong>{{ row.title }}</strong><small>{{ row.workPackageNumber }} · {{ row.activityType }}</small></td>
          <td>{{ row.locationName || 'Not specified' }}</td>
          <td>{{ row.targetReference || row.targetType }}</td>
          <td>{{ assignedLabor(row) }} / {{ requiredLabor(row) }}</td>
          <td>{{ allocatedEquipment(row) }} / {{ requiredEquipment(row) }}</td>
          <td><p-tag [value]="row.status || 'DRAFT'" [severity]="packageStatus(row.status)"></p-tag></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No scheduled field work.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'assignments'"
    title="Resource assignments"
    subtitle="Compare required and allocated labor and equipment before package release."
    [resultText]="packages.length + ' packages'">
    <p-table [value]="packages" [paginator]="packages.length > 20" [rows]="20" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Package</th><th>Target</th><th>Labor coverage</th><th>Equipment coverage</th><th>Exceptions</th><th>Status</th><th></th></tr></ng-template>
      <ng-template pTemplate="body" let-row>
        <tr>
          <td><strong>{{ row.title }}</strong><small>{{ row.workPackageNumber }} · {{ row.activityType }}</small></td>
          <td>{{ row.targetReference || row.targetType }}</td>
          <td><strong>{{ assignedLabor(row) }} / {{ requiredLabor(row) }}</strong><small>assigned / required</small></td>
          <td><strong>{{ allocatedEquipment(row) }} / {{ requiredEquipment(row) }}</strong><small>allocated / required</small></td>
          <td><p-tag *ngIf="(row.blockers?.length || 0) > 0" [value]="row.blockers?.length + ' blockers'" severity="danger"></p-tag><span *ngIf="!(row.blockers?.length)">—</span></td>
          <td><p-tag [value]="row.status || 'DRAFT'" [severity]="packageStatus(row.status)"></p-tag></td>
          <td><p-button icon="fa-solid fa-eye" [rounded]="true" [text]="true" size="small" ariaLabel="View package" (onClick)="openPackage(row)"></p-button></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No package assignments available.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'approvals'"
    title="Submitted labor actuals"
    subtitle="Approval locks source quantities and triggers the configured finance handoff."
    [resultText]="pendingLabor.length + ' pending'">
    <div actions>
      <p-button *ngIf="canApprove && (pendingLabor.length || pendingEquipment.length)" label="Approve all submitted"
        icon="pi pi-check-circle" [loading]="bulkApprovalLoading" (onClick)="approveAllSubmitted()"></p-button>
    </div>
    <p-table [value]="pendingLabor" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Labor</th><th>Work order</th><th>Work date</th><th>Actual</th><th>Status</th><th class="action-column">Action</th></tr></ng-template>
      <ng-template pTemplate="body" let-row>
        <tr><td><strong>{{ row.workerName || 'Field worker' }}</strong></td><td>{{ row.workPackageNumber }}</td>
          <td>{{ row.workDate | date:'mediumDate' }}</td><td>{{ row.actualQuantity }} {{ row.billingUnit }}</td>
          <td><p-tag value="SUBMITTED" severity="warn"></p-tag></td>
          <td class="action-column">
            <div class="approval-actions" *ngIf="canApprove">
              <input pInputText [(ngModel)]="approvalReasons['labor-' + row.fieldLaborWorkEntryId]" placeholder="Rejection reason">
              <p-button label="Reject" icon="pi pi-times" severity="danger" [outlined]="true" size="small" (onClick)="rejectLabor(row)"></p-button>
              <p-button label="Approve" icon="pi pi-check" size="small" (onClick)="approveLabor(row)"></p-button>
            </div>
          </td></tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No labor actuals are waiting for approval.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'approvals'"
    title="Submitted equipment usage"
    subtitle="Usage remains traceable to the equipment allocation, custodian, and work package."
    [resultText]="pendingEquipment.length + ' pending'">
    <p-table [value]="pendingEquipment" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Usage</th><th>Allocation</th><th>Work date</th><th>Actual</th><th>Status</th><th class="action-column">Action</th></tr></ng-template>
      <ng-template pTemplate="body" let-row>
        <tr><td><strong>#{{ row.fieldEquipmentUsageId }}</strong></td><td>#{{ row.fieldEquipmentAllocationId }}</td>
          <td>{{ row.workDate | date:'mediumDate' }}</td><td>{{ row.actualQuantity }} {{ row.billingUnit }}</td>
          <td><p-tag value="SUBMITTED" severity="warn"></p-tag></td>
          <td class="action-column">
            <div class="approval-actions" *ngIf="canApprove">
              <input pInputText [(ngModel)]="approvalReasons['equipment-' + row.fieldEquipmentUsageId]" placeholder="Rejection reason">
              <p-button label="Reject" icon="pi pi-times" severity="danger" [outlined]="true" size="small" (onClick)="rejectEquipment(row)"></p-button>
              <p-button label="Approve" icon="pi pi-check" size="small" (onClick)="approveEquipment(row)"></p-button>
            </div>
          </td></tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No equipment usage is waiting for approval.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'equipment'"
    title="Tools & equipment register"
    subtitle="Reusable operational resources only; vehicles remain in Fleet and consumables remain in Warehousing."
    [resultText]="resources.length + ' resources'">
    <div actions *ngIf="canManageEquipment">
      <p-button label="Add equipment resource" icon="pi pi-plus" (onClick)="openResourceDialog()"></p-button>
    </div>
    <p-table [value]="resources" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Resource</th><th>Category</th><th>Tracking</th><th>Ownership</th><th>Capacity</th><th>Location</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.resourceCode }}</strong><small>{{ row.resourceName }}</small></td><td>{{ row.categoryName }}</td><td>{{ row.trackingMode }}</td>
        <td><p-tag [value]="row.ownershipType" [severity]="ownershipSeverity(row.ownershipType)"></p-tag></td><td>{{ row.capacity }}</td>
        <td>{{ row.currentLocationName || '—' }}</td><td><strong>{{ row.operationalStatus }}</strong><small>{{ row.conditionStatus }}</small></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No reusable equipment registered.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <!-- Retired: labor rates are configured only in Workforce Finance. -->
  <!--
  <app-page-data-panel *ngIf="page === 'labor-rates'"
    title="Field labor rate cards"
    subtitle="Operational buy and sell rates are mastered in Field Work. They are independent of HR payroll and employee compensation."
    [resultText]="laborRates.length + ' rate cards'">
    <div actions *ngIf="canManageLaborRates">
      <p-button label="Add rate card" icon="pi pi-plus" (onClick)="openLaborRateDialog()"></p-button>
    </div>
    <p-table [value]="laborRates" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Rate</th><th>Scope</th><th>Unit</th><th>Buy</th><th>Sell</th><th>Effective</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.rateCode }}</strong><small>{{ row.rateName }}</small></td>
        <td><strong>{{ row.fieldWorkerId ? 'Person #' + row.fieldWorkerId : row.fieldCapabilityId ? row.capabilityName : row.sourceType || row.workerCategory || 'Tenant default' }}</strong>
          <small>{{ row.activityType || row.targetType || 'All field work' }}</small></td>
        <td>{{ row.billingUnit }}</td>
        <td>{{ row.buyRate | number:'1.2-2' }} {{ row.currencyCode }}</td>
        <td>{{ row.sellRate == null ? '—' : (row.sellRate | number:'1.2-2') + ' ' + row.currencyCode }}</td>
        <td>{{ row.effectiveFrom | date:'mediumDate' }}<small *ngIf="row.effectiveUntil">to {{ row.effectiveUntil | date:'mediumDate' }}</small></td>
        <td><p-tag [value]="row.active === false ? 'INACTIVE' : 'ACTIVE'" [severity]="row.active === false ? 'secondary' : 'success'"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No field labor rates configured.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  -->
  <app-page-data-panel *ngIf="page === 'rates'"
    title="Equipment rate cards"
    subtitle="Rates are resolved by resource, category and ownership, then tenant default."
    [resultText]="rates.length + ' rate cards'">
    <div actions *ngIf="canManageRates">
      <p-button label="Add rate card" icon="pi pi-plus" (onClick)="openRateDialog()"></p-button>
    </div>
    <p-table [value]="rates" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Rate</th><th>Scope</th><th>Unit</th><th>Buy</th><th>Sell</th><th>Effective</th><th>Status</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><strong>{{ row.rateCode }}</strong><small>{{ row.rateName }}</small></td><td>{{ row.equipmentCategoryId ? 'Category' : 'Tenant default' }}</td>
        <td>{{ row.billingUnit }}</td><td>{{ row.buyRate | number:'1.2-2' }} {{ row.currencyCode }}</td><td>{{ row.sellRate | number:'1.2-2' }} {{ row.currencyCode }}</td>
        <td>{{ row.effectiveFrom | date:'mediumDate' }}<small *ngIf="row.effectiveUntil">to {{ row.effectiveUntil | date:'mediumDate' }}</small></td>
        <td><p-tag [value]="row.active === false ? 'INACTIVE' : 'ACTIVE'" [severity]="row.active === false ? 'secondary' : 'success'"></p-tag></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No equipment rates configured.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'exceptions'"
    title="Finance exceptions"
    subtitle="Retryable failures remain linked to the approved labor or equipment source."
    [resultText]="exceptions.length + ' exceptions'">
    <p-table [value]="exceptions" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Source</th><th>Source ID</th><th>Error</th><th>Bulk cost</th><th>Payable</th><th>Receivable</th><th class="action-column">Action</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td><p-tag [value]="row.sourceKind || 'FIELD'" severity="info"></p-tag></td><td>#{{ row.sourceId }}</td>
        <td><strong>{{ row.errorCode }}</strong><small>{{ row.errorMessage }}</small></td><td>{{ row.bulkCostId || '—' }}</td><td>{{ row.payableId || '—' }}</td><td>{{ row.receivableId || '—' }}</td>
        <td class="action-column"><p-button *ngIf="canGenerateFinance" label="Retry" icon="pi pi-refresh" size="small" [outlined]="true" (onClick)="retryException(row)"></p-button></td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="7" class="empty-state">No finance exceptions.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>

  <app-page-data-panel *ngIf="page === 'exceptions'"
    title="Settlement traceability"
    subtitle="Generated finance lines remain grouped by settlement period with direct bulk cost, payable, and receivable references."
    [resultText]="settlements.length + ' periods'">
    <p-table [value]="settlements" responsiveLayout="scroll">
      <ng-template pTemplate="header"><tr><th>Period</th><th>Generated lines</th><th>Exceptions</th><th>Bulk costs</th><th>Payables</th><th>Receivables</th></tr></ng-template>
      <ng-template pTemplate="body" let-row><tr>
        <td>{{ row.periodStart | date:'mediumDate' }} – {{ row.periodEnd | date:'mediumDate' }}</td>
        <td>{{ row.generatedLines || 0 }}</td><td>{{ row.exceptionLines || 0 }}</td>
        <td>{{ (row.bulkCostIds || []).join(', ') || '—' }}</td>
        <td>{{ (row.payableIds || []).join(', ') || '—' }}</td>
        <td>{{ (row.receivableIds || []).join(', ') || '—' }}</td>
      </tr></ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="6" class="empty-state">No finance settlements have been generated.</td></tr></ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>

<app-field-person-create-dialog
  [(visible)]="personDialogVisible" [saving]="savingPerson" [error]="error"
  [employees]="employees" [suppliers]="supplierClients" [parties]="parties"
  [newlyCreatedLaborer]="newlyCreatedLaborer"
  (create)="savePerson($event)" (createLaborer)="openLaborerQuickCreate()">
</app-field-person-create-dialog>

<app-party-quick-create-dialog [(visible)]="laborerQuickCreateVisible" mode="laborer"
  [companyRequired]="false" (created)="laborerCreated($event)">
</app-party-quick-create-dialog>

<app-field-worker-capability-dialog
  [(visible)]="workerCapabilityDialogVisible" [saving]="savingWorkerCapability" [error]="error"
  [people]="capabilityPeople" [capabilities]="capabilities" [proficiencyLevels]="proficiencyLevels"
  (searchPeople)="searchCapabilityPeople($event)" (assign)="saveWorkerCapability($event)">
</app-field-worker-capability-dialog>

<app-equipment-resource-create-dialog
  [(visible)]="resourceDialogVisible" [saving]="savingResource" [error]="error"
  [categories]="categories" [suppliers]="supplierClients" [parties]="parties"
  (create)="saveResource($event)">
</app-equipment-resource-create-dialog>

<app-equipment-rate-create-dialog
  [(visible)]="rateDialogVisible" [saving]="savingRate" [error]="error"
  [resources]="resources" [categories]="categories" [customers]="rateClients"
  (create)="saveRate($event)">
</app-equipment-rate-create-dialog>

<!-- Retired: Workforce Finance is the canonical labor rate-card editor.
<app-labor-rate-create-dialog
  [(visible)]="laborRateDialogVisible" [saving]="savingLaborRate" [error]="error"
  [people]="people" [capabilities]="capabilities" [customers]="rateClients"
  (create)="saveLaborRate($event)">
</app-labor-rate-create-dialog>
-->

<app-field-work-setup-create-dialog
  [(visible)]="setupDialogVisible" [type]="setupDialogType" [saving]="savingSetup" [error]="error" [capabilities]="capabilities"
  (create)="saveSetup($event)">
</app-field-work-setup-create-dialog>
